import { isNil } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { TextStdRg } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { log } from '@/logger/logger.ts';
import { clampValue, roundToDecimalPlaces } from '@/utils/numbers';
import { parseDurationSeconds } from '@/utils/parse-duration-seconds';
import { getMimeTypeFromExtension } from '@/utils/fileTypes';
import { canonicalizeModelId } from '@/utils/model-name.utils';
import { resolvePromotionForModel, type ModelPromotion } from '@/utils/modelPromotions';
import { ModelType } from '@/enums/model-type.enum';
import { GPT_IMAGE_MODEL_TYPES } from '@/consts/gpt-image.consts';
import { NodeType } from '@/enums/node-type.enum';
import { LOCAL_MODEL_KEY_PREFIX } from '@/consts/localModel.consts';
import { LIST_SELECTOR_NODE_ID, PROMPT_NODE_ID } from '@/consts/node-types.consts';
import { useWorkflowStore } from '@/state/workflow.state';
import { RunType } from '@/enums/run-type.enum';
import ThreeDeeViewer from './LazyThreeDeeViewer';
import type { MediaAsset, UploadAssetResponse } from '@/types/api/assets';
import type { Model, ModelBaseNodeData } from '@/types/nodes/model';
import type { WorkflowRole, ModelPricesMaps } from '@/state/workflow.state';
import type { ModelPrice } from '@/types/api/modelPrice';
import type { BaseNodeData, Edge, MergeAlphaData, Node, Schema } from '@/types/node';

const logger = log.getLogger('Utils');

export const getImageDimensions = async (image: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      resolve({ width, height });
    };

    img.onerror = (error) => {
      logger.error('Failed to load image', error);
      reject(new Error('Failed to load image'));
    };

    img.src = image;
  });
};

const isMergeAlphaData = (data: unknown): data is MergeAlphaData => {
  return !!data && typeof data === 'object' && 'inputNode' in data;
};

const isBase64DataUrl = (value: string) => {
  return value.includes('data:image/png;base64') || value.includes('data:image/jpeg;base64');
};

const cleanBase64DataUrlsInPlace = (root: unknown) => {
  const sanitize = (value: unknown) => {
    if (!value || typeof value === 'string') {
      return;
    }

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        if (typeof item === 'string' && isBase64DataUrl(item)) {
          value[i] = '';
          continue;
        }
        sanitize(item);
      }
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    const record = value as Record<string, unknown>;
    for (const [key, child] of Object.entries(record)) {
      if (typeof child === 'string') {
        if (isBase64DataUrl(child)) {
          record[key] = '';
        }
        continue;
      }
      sanitize(child);
    }
  };

  sanitize(root);
};

// Keep temp `local:` 3D files out of persisted state — they have no server
// backing yet and would deserialize to a broken URL on reload. Sweeps the whole
// node so it covers every file-bearing shape (arrays like ImportNode `files[]` and
// MediaIteratorNode's Parameter-wrapped values, as well as single-file fields),
// including future node types — not just the two known ones.
const stripLocalDirectLoadFilesInPlace = (root: unknown) => {
  // A 3D file still pointing at its temp `local:` key (upload not yet finished).
  const isLocalDirectLoadFile = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') return false;
    const record = value as { type?: unknown; url?: unknown };
    return record.type === '3D' && typeof record.url === 'string' && record.url.startsWith(LOCAL_MODEL_KEY_PREFIX);
  };

  const sanitize = (value: unknown) => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      // Drop temp files from the array, then recurse into whatever survives.
      for (let i = value.length - 1; i >= 0; i--) {
        if (isLocalDirectLoadFile(value[i])) {
          value.splice(i, 1);
        } else {
          sanitize(value[i]);
        }
      }
      return;
    }

    const record = value as Record<string, unknown>;
    for (const [key, child] of Object.entries(record)) {
      if (isLocalDirectLoadFile(child)) {
        record[key] = undefined;
      } else {
        sanitize(child);
      }
    }
  };

  sanitize(root);
};

function sanitizeNodes(nodesToSanitize: Node[]) {
  const clonedNodes = cloneDeep(nodesToSanitize);
  // Now you can safely modify sanitizedNode since it's deeply cloned
  return clonedNodes.map((node) => {
    stripLocalDirectLoadFilesInPlace(node);

    if (
      node.type === 'compv3' &&
      'data' in node.data &&
      node.data.data &&
      typeof node.data.data === 'object' &&
      'input' in node.data.data &&
      Array.isArray(node.data.data.input)
    ) {
      for (const [i, input] of node.data.data.input.entries()) {
        // dynamic compositor input will be filled in at runtime
        if (input[1]?.file?.url.includes('data:image/png;base64')) {
          node.data.data.input[i][1].file = undefined;
        }
      }
    }
    if (isMergeAlphaData(node.data) && node.data.inputNode?.file?.url.includes('data:image/png;base64')) {
      node.data.inputNode.file = undefined;
    }
    if (isMergeAlphaData(node.data) && node.data.alphaInput?.file?.url.includes('data:image/png;base64')) {
      node.data.alphaInput.file = undefined;
    }

    // Keep the targeted handling above intact; this catches any remaining base64 data-URLs anywhere else.
    cleanBase64DataUrlsInPlace(node);

    // `hidden` is a transient, canvas-only React Flow flag (e.g. the template gallery stages previews
    // hidden) — never persist it to the server.
    delete node.hidden;

    return node;
  });
}

const sanitizeEdges = (edgesToSanitize: Edge[], nodes: Node[]) => {
  const nodeMap = new Set(nodes.map((node) => node.id));
  return edgesToSanitize
    .filter((edge) => nodeMap.has(edge.source) && nodeMap.has(edge.target))
    .map((edge) => {
      // `hidden` is a transient, canvas-only React Flow flag — strip it without mutating the live edge.
      const clone = { ...edge };
      delete clone.hidden;
      return clone;
    });
};

// Tour scaffolding that lives only on the canvas — never persist it (an orphan renders as a
// forever-pulsing indicator for every collaborator on the file).
const EPHEMERAL_NODE_TYPES = new Set<string>([NodeType.Target]);

export const sanitizeRecipe = (nodes: Node[], edges: Edge[]) => {
  const sanitizedNodes = sanitizeNodes(nodes).filter((node) => !EPHEMERAL_NODE_TYPES.has(node.type ?? ''));
  const sanitizedEdges = sanitizeEdges(edges, sanitizedNodes);

  return { nodes: sanitizedNodes, edges: sanitizedEdges };
};

type NodeDataWithRunOutputs = BaseNodeData & { latestPrediction?: unknown };

/**
 * Prepare nodes for POST /cost. Drops previous-run `output` / `result` / `latestPrediction`
 * before clone so we don't copy pixel blobs, then runs {@link cleanBase64DataUrlsInPlace} on
 * the rest (iterator files, `iteratorInput`, leftover `input`). Do not reuse {@link sanitizeNodes}:
 * that also drops in-flight `local:` 3D files and would undercount iterator runs.
 */
export const sanitizeNodesForCost = (nodes: Node[]): Node[] => {
  const withoutRunOutputs = nodes.map((node) => {
    const data = node.data as NodeDataWithRunOutputs | undefined;
    if (!data) {
      return node;
    }
    return { ...node, data: omit(data, ['output', 'result', 'latestPrediction']) };
  });
  const clonedNodes = cloneDeep(withoutRunOutputs);
  for (const node of clonedNodes) {
    cleanBase64DataUrlsInPlace(node);
  }
  return clonedNodes;
};

// bria register image
export const registerImageUtil = async (url: string) => {
  const res = await safeAxios.post<{ visualId: string }>(API_ROUTES.models.imageRegister(), { url });

  return res.data.visualId;
};

/**
 * Negates the colors of an image from a URL or base64 string.
 * @param {string} imageSource - URL or base64 string of the image
 * @returns {Promise<string>} Promise that resolves to a base64 string of the negated image
 */
export async function negateImageColors(imageSource: string) {
  // Create an off-screen canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Create and load the image
  const image = new Image();
  image.crossOrigin = 'Anonymous'; // Enable CORS if needed

  // Wait for the image to load
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSource;
  });

  // Set canvas size to match image
  canvas.width = image.width;
  canvas.height = image.height;

  // Draw image to canvas
  ctx.drawImage(image, 0, 0);

  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Invert colors
  for (let i = 0; i < imageData.data.length; i += 4) {
    imageData.data[i] = 255 - imageData.data[i]; // Red
    imageData.data[i + 1] = 255 - imageData.data[i + 1]; // Green
    imageData.data[i + 2] = 255 - imageData.data[i + 2]; // Blue
    // Alpha channel (i + 3) remains unchanged
  }

  // Put the negated image data back
  ctx.putImageData(imageData, 0, 0);

  // Return as base64 string
  return canvas.toDataURL('image/png');
}

export const getHandleId = (nodeId, handleType, handleName) => {
  return `${nodeId}-${handleType}-${handleName}`;
};

/**
 * Inverse of {@link getHandleId}. Strips the `${nodeId}-${side}-` prefix to recover the handle
 * key, returning null if the prefix doesn't match (defensive against node IDs that themselves
 * contain `-input-` or `-output-`).
 */
export const parseHandleKey = (handleId: string, nodeId: string, side: 'input' | 'output'): string | null => {
  const prefix = `${nodeId}-${side}-`;
  return handleId.startsWith(prefix) ? handleId.slice(prefix.length) : null;
};

/// programmatic node creation utils
export const getNodeTemplates = (type: string, param: unknown, schema: Schema) => {
  switch (type) {
    case 'number':
    case 'integer':
    case 'input-number':
    case 'input-integer': {
      const mode = schema.type === 'integer' || schema.type === 'input-integer' ? 'integer' : 'number';

      return {
        handleName: 'number',
        template: {
          id: 'JbQzwNX9qVhHP59o5yS1Clone',
          type: 'number',
          initialData: {
            name: schema.title,
            description: schema.description,
            value: clampValue(Number(param), Number(schema.min), Number(schema.max)),
            min: schema.min,
            max: schema.max,
            step: schema.step,
            mode: mode,
          },
        },
      };
    }
    case 'boolean':
      return {
        handleName: 'option',
        template: {
          id: 'UpdngxSEck7aHx92LkSgClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            value: param,
          },
        },
      };
    case 'enum': {
      const isNumber = (schema.options as unknown[]).every((item) => typeof item === 'number');
      const i = (schema.options as unknown[]).indexOf(param);
      const index = i >= 0 && schema.options && i < schema.options.length ? i : 0;
      return {
        handleName: 'option',
        template: isNumber
          ? {
              id: 'number_selector',
              initialData: {
                name: schema.title,
                description: schema.description,
                selectedIndex: index,
                params: {
                  options: schema.options,
                },
              },
            }
          : {
              id: LIST_SELECTOR_NODE_ID,
              initialData: {
                name: schema.title,
                description: schema.description,
                selectedIndex: index,
                params: {
                  options: schema.options,
                },
                list: schema.options,
              },
            },
      };
    }
    case 'fal_image_size': {
      // "Custom"/"Default" are UI sentinels for free-form width/height, which a list can't drive.
      const options = (schema.options ?? []).filter((option) => option !== 'Custom' && option !== 'Default');
      return {
        handleName: 'option',
        template: {
          id: LIST_SELECTOR_NODE_ID,
          initialData: {
            name: schema.title,
            description: schema.description,
            selectedIndex: Math.max(options.indexOf(String(param)), 0),
            params: {
              options,
            },
            list: options,
          },
        },
      };
    }
    case 'text': {
      const prompt = typeof param === 'string' ? param : '';
      return {
        handleName: 'prompt',
        template: {
          id: PROMPT_NODE_ID,
          initialData: { prompt },
        },
      };
    }
    case 'array':
      return {
        handleName: 'array',
        template: {
          id: 'E4Lo1vBJYlSjvgs0LtcSClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            array: param ? (Array.isArray(param) ? param.map(String) : param) : [''],
            placeholder: `${String(schema.title)} item`,
          },
        },
      };
    case 'seed':
      return {
        handleName: 'seed',
        template: {
          id: 'HMTea9nBdmeBeoCAdxvzClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            isRandom: (param as { isRandom: boolean }).isRandom,
            seed: (param as { seed: number }).seed,
          },
        },
      };
  }
};

/**

Determines the model type based on the provided model object.
@param {Object} model - The model object to evaluate.
@param {'replicate' | 'civit' | null=} model.service - The service type of the model.
@param {string} model.name - The name of the model.
@returns {ModelType} The determined model type.
*/
export const getModelType = (model: Partial<Model>) => {
  if (
    model.service === ModelType.Replicate.valueOf() ||
    model.service === ModelType.Civit.valueOf() ||
    model.service === ModelType.FalImported.valueOf()
  ) {
    return model.service as ModelType;
  }

  const enumValues = Object.values(ModelType).map((value) => value.valueOf());
  if (model.name && enumValues.includes(model.name)) {
    return model.name as ModelType;
  }

  if (model.name?.includes('/civitai.com')) {
    return ModelType.Civit;
  }

  if (model.name?.includes('/fal.ai') || model.name?.includes('fal-ai')) {
    return ModelType.FalImported;
  }

  return ModelType.Replicate;
};

const findPriceByVariantAndExtraData = (
  modelPrices: ModelPrice[],
  modelName: string | null,
  extraData: string | null,
) => {
  if (modelName && extraData) {
    const exactMatch = modelPrices.find((obj) => obj.modelVariant === modelName && obj.extraData === extraData);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Priority 2: Match modelVariant only
  if (modelName) {
    const variantMatch = modelPrices.find((obj) => obj.modelVariant === modelName);
    if (variantMatch) {
      return variantMatch;
    }
  }
  // Priority 3: Match extraData only
  if (extraData) {
    const extraDataMatch = modelPrices.find((obj) => obj.extraData === extraData);
    if (extraDataMatch) {
      return extraDataMatch;
    }
  }
};

export const RELEVANT_PRICING_MODEL_PARAMS = [
  'duration',
  'seconds',
  'quality',
  'motion_mode',
  'rendering_speed',
  'resolution',
  'model',
  'generate_audio',
  'version',
  'texture_quality',
  'enable_texture',
  'enable_pbr',
  'target_fps',
  'preset',
  'enable_detail_refine',
  'video_quality',
  'num_frames',
  'frames_per_second',
  'us_hosted',
  'number_of_images',
  'image_size',
] as const;

const KLING_AUDIO_MULTIPLIER = 1.5;
const SEEDANCE2_AUDIO_MULTIPLIER = 1;
const SEEDANCE2_1080P_MULTIPLIER = 2.25;
const SEEDANCE2_4K_MULTIPLIER = 6.0;
// Seedance 2.5: base credits are per-second at 720p; reference scales differently (45 -> 21) from
// the generation endpoints (28 -> 13). Keep in sync with model-price.consts.ts.
const SEEDANCE_25_480P_MULTIPLIER = 13 / 28;
const SEEDANCE_25_REF_480P_MULTIPLIER = 21 / 45;
const SEEDANCE_25_1080P_MULTIPLIER = 68 / 28;
const SEEDANCE_25_REF_1080P_MULTIPLIER = 109 / 45;
const SEEDANCE_US_HOSTED_MULTIPLIER = 1.3;
// H3 credits/second. Keep in sync with model-price.consts.ts.
const MINIMAX_H3_480P_MULTIPLIER = 4 / 5;
const MINIMAX_H3_2K_MULTIPLIER = 11 / 5;
const MINIMAX_H3_4K_MULTIPLIER = 13 / 5;
const MINIMAX_H3_MAX_480P_MULTIPLIER = 4 / 7;
const MINIMAX_H3_MODEL_PREFIX = 'minimax/h3';
const MINIMAX_H3_DEFAULT_TIER = 'Standard';
const MINIMAX_H3_MAX_TIER = 'Max';
// WAN 3.0 credits/second after the 30% Fal discount, anchored at 1080p. Keep in sync with model-price.consts.ts.
const WAN_30_480P_MULTIPLIER = 4 / 17;
const WAN_30_720P_MULTIPLIER = 8 / 17;
const WAN_30_PRIME_480P_MULTIPLIER = 6 / 24;
const WAN_30_PRIME_720P_MULTIPLIER = 12 / 24;
const WAN_30_MODEL_PREFIX = 'alibaba/wan-3.0';
const WAN_30_PRIME_SLUG_SEGMENT = 'wan-3.0-prime/';
const WAN_30_PRIME_TIER = 'Prime';
// Omni 1.1 credits/second, anchored at 720p. Keep in sync with model-price.consts.ts.
const GEMINI_OMNI_11_360P_MULTIPLIER = 4 / 11;
const GEMINI_OMNI_11_1080P_MULTIPLIER = 16 / 11;
const GEMINI_OMNI_11_4K_MULTIPLIER = 33 / 11;
const GEMINI_OMNI_PREVIEW_MODEL_PREFIX = 'google/gemini-omni-flash-preview';
const GEMINI_OMNI_11_VARIANT = '1.1';
// Luma Ray 3.2: base credits anchored at 540p; higher resolutions scale up. Keep in sync with model-price.consts.ts.
const LUMA_RAY_32_720P_MULTIPLIER = 2;
const LUMA_RAY_32_1080P_MULTIPLIER = 8;
const TOPAZ_VIDEO_MAX_BASE_FPS = 30;
const TOPAZ_VIDEO_HIGH_FPS_MULTIPLIER = 176 / 118;
const VEO_31_4K_RESOLUTION_MULTIPLIER = 3;
const LTX_23_1440P_MULTIPLIER = 2;
const LTX_23_2160P_MULTIPLIER = 4;
const FLUX_3_RESOLUTION_MULTIPLIERS: Record<string, number> = {
  '720p': 1,
  '1080p': 24 / 14,
  '1440p': 27 / 14,
  '2160p': 40 / 14,
};
const FLUX_3_EXTEND_1080P_MULTIPLIER = 64 / 49;
const FLUX_3_PRICED_MODELS: ReadonlySet<string> = new Set<string>([
  'blackforestlabs/flux-3/text-to-video',
  'blackforestlabs/flux-3/image-to-video',
  'blackforestlabs/flux-3/first-last-frame-to-video',
  'blackforestlabs/flux-3/keyframes-to-video',
]);
const FLUX_3_EXTEND_MODEL = 'blackforestlabs/flux-3/extend-video';
const FLUX_3_VIDEO_MODEL = 'blackforestlabs/flux-3/text-to-video';
const FLUX_3_MAX_1080P_MULTIPLIER = 24 / 14;
const KLING_V4_1080P_MULTIPLIERS: Record<string, number> = {
  'kling/v4/text-to-video': 20 / 13,
  'kling/v4/image-to-video': 20 / 13,
  'kling/v4/reference-to-video': 24 / 16,
  'kling/v4/video-to-video/edit': 24 / 16,
};
const VEED_SUBTITLES_DYNAMIC_PRESET_MULTIPLIER = 2;
// LTX Render to Real: base credits are the flat price at 480p with detail refine off, scaled by
// resolution and detail refine. Keep in sync with model-price.consts.ts.
const LTX_RENDER_TO_REAL_720P_MULTIPLIER = 3;
const LTX_RENDER_TO_REAL_DETAIL_REFINE_MULTIPLIER = 2;
// LTX 2.3 LoRA: base credits are one second at `medium`. Keep in sync with model-price.consts.ts.
const LTX_23_LORA_MODEL = 'fal-ai/ltx-2.3-quality/text-to-video/lora';
const LTX_23_LORA_DEFAULT_QUALITY = 'high';
const LTX_23_LORA_DEFAULT_FRAMES = 121;
const LTX_23_LORA_DEFAULT_FPS = 24;
const LTX_23_LORA_QUALITY_MULTIPLIERS: Record<string, number> = {
  low: 4 / 6,
  medium: 1,
  high: 25 / 6,
  maximum: 57 / 6,
};
const CASSETTE_AI_MUSIC_GENERATOR = 'cassetteai/music-generator';
// CassetteAI Music Generator prices by duration tier rather than one row per value. Keep in sync
// with model-price.consts.ts.
const CASSETTE_AI_MUSIC_DURATION_TIERS = [
  { maxSeconds: 60, extraData: '0-60' },
  { maxSeconds: 120, extraData: '61-120' },
  { maxSeconds: 180, extraData: '121-180' },
] as const;
const KREA_3_MODEL = 'krea/v3/text-to-image';
// Krea 3 prices by output pixel-count tier. Keep in sync with model-price.consts.ts.
const KREA_3_SIZE_TIERS = [
  { maxPixels: 576 * 1024, extraData: '768p' },
  { maxPixels: 1024 * 1024, extraData: '1k' },
  { maxPixels: 2048 * 2048, extraData: '2k' },
  { maxPixels: Infinity, extraData: '4k' },
] as const;
const KREA_3_AUTO_SIZE_EXTRA_DATA = 'auto';
const KREA_3_SIZE_STEP = 16;
const KREA_3_PRESET_SIZE_PIXELS: Record<string, number> = {
  square_hd: 1024 * 1024,
  square: 512 * 512,
  portrait_4_3: 768 * 1024,
  portrait_16_9: 576 * 1024,
  landscape_4_3: 1024 * 768,
  landscape_16_9: 1024 * 576,
};
// Fallback for an unpriced video model, which the server charges instead of the flat default. Other
// categories still use `defaultPrice`. Keep in sync with model-price.consts.ts.
const DEFAULT_VIDEO_MODEL_PRICE = 50;

export type ResolvedModelPrice = {
  credits: number;
  /** Undiscounted credits; set only when a promotion lowered `credits`. */
  fullCredits?: number;
  /** Set only when a promotion discounted `credits`. */
  promo?: { expiresAt?: string };
  isByok: boolean;
  isPerSecond?: boolean;
  /** True when no configured price row matched. */
  isFallbackPrice?: boolean;
};

const getAudioMultiplier = (modelName: string | null, modelType: ModelType, inputModel: string | null) => {
  const isKlingV3 =
    modelName?.includes('kling-video/v3/') ||
    modelName?.includes('kling-video/o3/') ||
    (modelType === ModelType.Kling && inputModel?.includes('3.0'));

  const isSeedance2 = modelName?.includes('seedance-2.0');
  if (isKlingV3) {
    return KLING_AUDIO_MULTIPLIER;
  }
  if (isSeedance2) {
    return SEEDANCE2_AUDIO_MULTIPLIER;
  }
  return 1;
};

const getResolutionMultiplier = (modelName: string | null, resolution: string | null, inputModel: string | null) => {
  const klingV4Multiplier = modelName ? KLING_V4_1080P_MULTIPLIERS[modelName] : undefined;
  if (klingV4Multiplier !== undefined) {
    return resolution === '720p' ? 1 : klingV4Multiplier;
  }
  const isSeedance2 = modelName?.includes('seedance-2.0');
  if (isSeedance2 && resolution === '4k') {
    return SEEDANCE2_4K_MULTIPLIER;
  }
  if (isSeedance2 && resolution === '1080p') {
    return SEEDANCE2_1080P_MULTIPLIER;
  }
  if (modelName?.includes('seedance-2.5')) {
    const isReference = modelName.includes('/reference-to-video');
    if (resolution === '480p') {
      return isReference ? SEEDANCE_25_REF_480P_MULTIPLIER : SEEDANCE_25_480P_MULTIPLIER;
    }
    if (resolution === '1080p') {
      return isReference ? SEEDANCE_25_REF_1080P_MULTIPLIER : SEEDANCE_25_1080P_MULTIPLIER;
    }
  }
  if (modelName?.includes('google-veo3') && resolution === '4k') {
    return VEO_31_4K_RESOLUTION_MULTIPLIER;
  }
  if (modelName === 'luma/ray-3-2') {
    // Base credits are anchored at 540p; 720p and 1080p scale up.
    if (resolution === '1080p') {
      return LUMA_RAY_32_1080P_MULTIPLIER;
    }
    if (resolution === '720p') {
      return LUMA_RAY_32_720P_MULTIPLIER;
    }
  }
  if (modelName?.startsWith(GEMINI_OMNI_PREVIEW_MODEL_PREFIX) && inputModel === GEMINI_OMNI_11_VARIANT) {
    const omniResolution = resolution?.toLowerCase();
    if (omniResolution === '360p') {
      return GEMINI_OMNI_11_360P_MULTIPLIER;
    }
    if (omniResolution === '1080p') {
      return GEMINI_OMNI_11_1080P_MULTIPLIER;
    }
    if (omniResolution === '4k') {
      return GEMINI_OMNI_11_4K_MULTIPLIER;
    }
  }
  if (modelName?.startsWith(MINIMAX_H3_MODEL_PREFIX)) {
    const isMax = inputModel === MINIMAX_H3_MAX_TIER;
    const h3Resolution = resolution?.toUpperCase();
    if (h3Resolution === '480P') {
      return isMax ? MINIMAX_H3_MAX_480P_MULTIPLIER : MINIMAX_H3_480P_MULTIPLIER;
    }
    // Max is 480P/768P only, so these stay Standard-only.
    if (h3Resolution === '2K') {
      return MINIMAX_H3_2K_MULTIPLIER;
    }
    if (h3Resolution === '4K') {
      return MINIMAX_H3_4K_MULTIPLIER;
    }
  }
  if (modelName?.startsWith(WAN_30_MODEL_PREFIX)) {
    // A raw prime endpoint carries no `model` input, so the slug is the only tier signal there.
    const isPrime = modelName.includes(WAN_30_PRIME_SLUG_SEGMENT) || inputModel === WAN_30_PRIME_TIER;
    const wanResolution = resolution?.toLowerCase();
    if (wanResolution === '480p') {
      return isPrime ? WAN_30_PRIME_480P_MULTIPLIER : WAN_30_480P_MULTIPLIER;
    }
    if (wanResolution === '720p') {
      return isPrime ? WAN_30_PRIME_720P_MULTIPLIER : WAN_30_720P_MULTIPLIER;
    }
  }
  if (modelName === 'fal-ai/ltx-2.3/text-to-video' || modelName === 'fal-ai/ltx-2.3/image-to-video') {
    if (resolution === '1440p') {
      return LTX_23_1440P_MULTIPLIER;
    }
    if (resolution === '2160p') {
      return LTX_23_2160P_MULTIPLIER;
    }
  }
  if (modelName && FLUX_3_PRICED_MODELS.has(modelName)) {
    if (modelName === FLUX_3_VIDEO_MODEL && inputModel === 'Max') {
      return resolution === '1080p' ? FLUX_3_MAX_1080P_MULTIPLIER : 1;
    }
    return FLUX_3_RESOLUTION_MULTIPLIERS[resolution ?? '720p'] ?? 1;
  }
  if (modelName === FLUX_3_EXTEND_MODEL && resolution === '1080p') {
    return FLUX_3_EXTEND_1080P_MULTIPLIER;
  }
  return 1;
};

/**
 * LTX Render to Real scales its base credits (480p, detail refine off) by resolution (3x for 720p)
 * and detail refine (2x when on). Mirrors the server `getLtxRenderToRealMultiplier` in model-price.service.ts.
 */
const getLtxRenderToRealMultiplier = (modelName: string | null, modelInput: ModelBaseNodeData['params']) => {
  if (modelName !== 'fal-ai/ltx-2.3-quality/render-to-real') {
    return 1;
  }
  // Fal defaults an omitted (and rejects an invalid) `resolution` to 720p, so charge the 720p
  // tier unless the caller explicitly requests 480p — mirrors the server multiplier.
  const resolutionMultiplier = modelInput?.resolution === '480p' ? 1 : LTX_RENDER_TO_REAL_720P_MULTIPLIER;
  const detailRefineMultiplier = modelInput?.enable_detail_refine ? LTX_RENDER_TO_REAL_DETAIL_REFINE_MULTIPLIER : 1;
  return resolutionMultiplier * detailRefineMultiplier;
};

const PER_IMAGE_PRICED_MODEL_TYPES = new Set<ModelType>([ModelType.GptImage1, ModelType.GptImage1Edit]);

/**
 * GPT Image runs generate `number_of_images` images and the price row holds one image's credits.
 * Mirrors the server `getImageCountMultiplier` in model-price.service.ts.
 */
const getImageCountMultiplier = (modelType: ModelType, modelInput: ModelBaseNodeData['params']) => {
  if (!PER_IMAGE_PRICED_MODEL_TYPES.has(modelType)) {
    return 1;
  }
  const count = Number(modelInput?.number_of_images || 1);
  return Number.isFinite(count) && count > 1 ? Math.ceil(count) : 1;
};

/**
 * LTX 2.3 LoRA has no duration param to key price rows on, so the single base row is one second at
 * `medium` and the clip length comes from the frame count. Mirrors the server
 * `getLtx23LoraMultiplier` in model-price.service.ts.
 */
const getLtx23LoraMultiplier = (modelName: string | null, modelInput: ModelBaseNodeData['params']) => {
  if (modelName !== LTX_23_LORA_MODEL) {
    return 1;
  }
  const quality = String(modelInput?.video_quality ?? LTX_23_LORA_DEFAULT_QUALITY);
  const qualityMultiplier =
    LTX_23_LORA_QUALITY_MULTIPLIERS[quality] ?? LTX_23_LORA_QUALITY_MULTIPLIERS[LTX_23_LORA_DEFAULT_QUALITY];
  const frames = Number(modelInput?.num_frames) || LTX_23_LORA_DEFAULT_FRAMES;
  const fps = Number(modelInput?.frames_per_second) || LTX_23_LORA_DEFAULT_FPS;
  return qualityMultiplier * (frames / fps);
};

/**
 * Seconds for a model that states its length as a frame count, for the BYOK per-second estimate.
 * Mirrors the server `resolveFrameCountSeconds` in model-price.service.ts.
 */
const resolveFrameCountSeconds = (
  modelName: string | undefined,
  modelInput: ModelBaseNodeData['params'],
): number | undefined => {
  if (modelName?.toLowerCase() !== LTX_23_LORA_MODEL) {
    return undefined;
  }
  const frames = Number(modelInput?.num_frames) || LTX_23_LORA_DEFAULT_FRAMES;
  const fps = Number(modelInput?.frames_per_second) || LTX_23_LORA_DEFAULT_FPS;
  return frames / fps;
};

const getTopazVideoMultiplier = (modelType: ModelType, modelInput: ModelBaseNodeData['params']): number => {
  if (modelType !== ModelType.TopazUpscaleVideo) {
    return 1;
  }
  const isHighFps = typeof modelInput?.target_fps === 'number' && modelInput.target_fps > TOPAZ_VIDEO_MAX_BASE_FPS;
  return isHighFps ? TOPAZ_VIDEO_HIGH_FPS_MULTIPLIER : 1;
};

const getSeedanceMultiplier = (modelName: string | null, usHosted: boolean) => {
  const isSeedance = modelName?.includes('bytedance/seedance-2.0');
  return isSeedance && usHosted ? SEEDANCE_US_HOSTED_MULTIPLIER : 1;
};

const getVeedSubtitlesDynamicPresetMultiplier = (
  modelName: string | null,
  modelInput: ModelBaseNodeData['params'],
): number => {
  if (modelName !== 'veed/subtitles') {
    return 1;
  }
  if (
    ['glass', 'whisper', 'glide', 'glide2', 'fusion', 'terminal', 'handwritten'].includes(modelInput?.preset as string)
  ) {
    return VEED_SUBTITLES_DYNAMIC_PRESET_MULTIPLIER;
  }
  return 1;
};

/**
 * Resolves the `extraData` key a `duration` input matches against. Most models persist one row per
 * duration value, so the raw value is used as-is; CassetteAI Music Generator exposes a continuous
 * slider and buckets into tiered rows instead. Mirrors model-price.service.ts.
 */
const getDurationExtraData = (
  modelName: string | null,
  duration: string | number | null | undefined,
): string | null => {
  if (isNil(duration)) {
    return null;
  }
  if (modelName !== CASSETTE_AI_MUSIC_GENERATOR) {
    return String(duration);
  }
  const seconds = Number(duration);
  if (!Number.isFinite(seconds)) {
    return String(duration);
  }
  const tier = CASSETTE_AI_MUSIC_DURATION_TIERS.find(({ maxSeconds }) => seconds <= maxSeconds);
  return (tier ?? CASSETTE_AI_MUSIC_DURATION_TIERS[CASSETTE_AI_MUSIC_DURATION_TIERS.length - 1]).extraData;
};

// Mirrors `getKrea3SizeExtraData` in model-price.service.ts.
const getKrea3SizeExtraData = (imageSize: unknown): string => {
  const size = imageSize as { width?: unknown; height?: unknown } | null | undefined;
  const roundUp = (value: unknown) => Math.ceil(Number(value) / KREA_3_SIZE_STEP) * KREA_3_SIZE_STEP;
  const pixels =
    typeof imageSize === 'string' ? KREA_3_PRESET_SIZE_PIXELS[imageSize] : roundUp(size?.width) * roundUp(size?.height);
  if (!pixels || !Number.isFinite(pixels)) {
    return KREA_3_AUTO_SIZE_EXTRA_DATA;
  }
  return KREA_3_SIZE_TIERS.find(({ maxPixels }) => pixels <= maxPixels)?.extraData ?? KREA_3_AUTO_SIZE_EXTRA_DATA;
};

// H3 tiers price independently. Mirrors `filterMinimaxH3TierRows` in model-price.service.ts.
const filterMinimaxH3TierRows = (modelPrices: ModelPrice[], inputModel: string | null): ModelPrice[] => {
  const tier = inputModel ?? MINIMAX_H3_DEFAULT_TIER;
  return modelPrices.filter((row) => (row.modelVariant ?? MINIMAX_H3_DEFAULT_TIER) === tier);
};

const getPriceRowExtraData = (priceName: string | null, modelInput: ModelBaseNodeData['params']): string | null => {
  if (priceName === KREA_3_MODEL) {
    return getKrea3SizeExtraData(modelInput?.image_size);
  }
  if (priceName === 'hitem3d/hi3d/v3.0/multi-view-to-3d') {
    const resolution = modelInput?.resolution ?? '2048quality';
    if (modelInput?.enable_texture === false) {
      return `${resolution}:geometry`;
    }
    if (modelInput?.enable_pbr === false) {
      return `${resolution}:texture`;
    }
    return `${resolution}:pbr`;
  }
  const duration = getDurationExtraData(priceName, modelInput?.duration);
  const quality = modelInput?.quality ? String(modelInput.quality) : null;
  const resolution = modelInput?.resolution ? String(modelInput.resolution) : null;
  const motion_mode = modelInput?.motion_mode ? String(modelInput.motion_mode) : null;
  const rendering_speed = modelInput?.rendering_speed ? String(modelInput.rendering_speed) : null;
  const version = modelInput?.version ? String(modelInput.version) : null;
  const texture_quality = modelInput?.texture_quality ? String(modelInput.texture_quality) : null;
  return duration || quality || motion_mode || rendering_speed || resolution || version || texture_quality;
};

const getModelPriceByVariantAndParams = (
  modelPrices: ModelPrice[],
  modelInput: ModelBaseNodeData['params'],
  inputModel: string | null,
  priceName: string | null,
): ModelPrice | undefined =>
  findPriceByVariantAndExtraData(modelPrices, inputModel, getPriceRowExtraData(priceName, modelInput));

type ModelPriceLookupOptions = {
  strict?: boolean;
  /**
   * When true, a `modelsByName` miss is retried with the control-char-stripped name.
   * Callers resolve the `normalize-imported-model-names` flag and pass it in — this
   * module stays free of the posthog dependency.
   */
  normalizeImportedModelNames?: boolean;
  /** Active promotions (from the promo flag payload). Callers resolve them and pass them in. */
  promotions?: ModelPromotion[];
  /** When true, promotions are never applied — user-imported models never get the promotion. */
  isImportedModel?: boolean;
  /**
   * Whether the node outputs video (see `isVideoOutputNode`). Only consulted when no price row
   * matched, to pick the same category fallback the server charges.
   */
  isVideoOutput?: boolean;
};

export const getModelPriceInfo = (
  model: Partial<Model>,
  modelPricesMaps: ModelPricesMaps,
  modelInput: ModelBaseNodeData['params'],
  options?: ModelPriceLookupOptions,
): ResolvedModelPrice | undefined => {
  if (!modelPricesMaps?.modelsByType?.size) {
    return undefined;
  }

  const modelType = getModelType(model);
  const modelName =
    modelType === ModelType.FalImported &&
    model.name &&
    canonicalizeModelId(model.name).toLowerCase() === 'hitem3d/hi3d/v3.0/image-to-3d'
      ? 'hitem3d/hi3d/v3.0/multi-view-to-3d'
      : model.name;
  let inputModel = modelInput?.model ? String(modelInput.model) : null;

  const strict = options?.strict === true;
  let modelPrices: ModelPrice[] | undefined;
  // Name used for multiplier matching below. Lowercased (the price map is case-insensitive) and
  // switched to the canonical form when the row is resolved via canonicalization, so exact-match
  // helpers (e.g. `luma/ray-3-2`, `veed/subtitles`) match regardless of the persisted name's case.
  // Mirrors model-price.service.ts.
  let priceName = modelName ? modelName.toLowerCase() : null;
  if (modelName && (modelType === ModelType.Replicate || modelType === ModelType.FalImported)) {
    // Case-insensitive lookup: the map is keyed by lowercased name, so uppercase variants
    // (e.g. `Bytedance/seedance-2.0`) still resolve to the same price row.
    modelPrices = modelPricesMaps.modelsByName.get(modelName.toLowerCase());
    if (!modelPrices?.length) {
      // Persisted names can carry stray whitespace/control chars/encoding around the owner or name
      // (incl. from later schema edits), so trim each `owner/name` segment and retry against the
      // clean, forward-slash-keyed price map.
      const canonical = canonicalizeModelId(modelName).toLowerCase();
      if (canonical !== modelName.toLowerCase()) {
        const retried = modelPricesMaps.modelsByName.get(canonical);
        if (retried?.length) {
          modelPrices = retried;
          priceName = canonical;
        }
      }
    }
  } else {
    modelPrices = modelPricesMaps.modelsByType.get(modelType);
  }
  if (!inputModel && priceName === 'fal-ai/nano-banana-2/edit') {
    inputModel = 'Nano Banana 2';
  }
  if (priceName === FLUX_3_VIDEO_MODEL) {
    inputModel = modelInput?.model === 'Max' ? 'Max' : 'Standard';
  }
  if (modelPrices?.length && priceName?.startsWith(MINIMAX_H3_MODEL_PREFIX)) {
    modelPrices = filterMinimaxH3TierRows(modelPrices, inputModel);
  }
  if (!modelPrices?.length) {
    if (strict) {
      return undefined;
    }
    const fallbackCredits = options?.isVideoOutput ? DEFAULT_VIDEO_MODEL_PRICE : modelPricesMaps.defaultPrice;
    return { credits: fallbackCredits, isByok: false, isFallbackPrice: true };
  }

  const audioMultiplier = modelInput?.generate_audio ? getAudioMultiplier(priceName, modelType, inputModel) : 1;
  const resolution = modelInput?.resolution ? String(modelInput.resolution) : null;
  const resolutionMultiplier = getResolutionMultiplier(priceName, resolution, inputModel);
  const topazVideoMultiplier = getTopazVideoMultiplier(modelType, modelInput);
  const veedSubtitlesDynamicPresetMultiplier = getVeedSubtitlesDynamicPresetMultiplier(priceName, modelInput);
  const ltxRenderToRealMultiplier = getLtxRenderToRealMultiplier(priceName, modelInput);
  const ltx23LoraMultiplier = getLtx23LoraMultiplier(priceName, modelInput);
  const seedanceMultiplier = getSeedanceMultiplier(priceName, modelInput?.us_hosted === true);
  const imageCountMultiplier = getImageCountMultiplier(modelType, modelInput);

  let entry: ModelPrice | undefined;
  if (modelPrices.length === 1) {
    entry = modelPrices[0];
  } else {
    /** Match the server's Medium price for Auto, even when the provider picks a higher quality. */
    const pricedInput =
      GPT_IMAGE_MODEL_TYPES.has(modelType) && modelInput?.quality === 'auto'
        ? { ...modelInput, quality: 'medium' }
        : modelInput;
    entry = getModelPriceByVariantAndParams(modelPrices, pricedInput, inputModel, priceName);
  }
  if (!entry) {
    if (strict) {
      return undefined;
    }
    entry = modelPrices[0];
  }

  const isByok = entry.isByok === true;
  if (isByok) {
    return { isByok, credits: entry.credits, isPerSecond: entry.isPerSecond };
  }

  const multiplier =
    audioMultiplier *
    resolutionMultiplier *
    topazVideoMultiplier *
    veedSubtitlesDynamicPresetMultiplier *
    ltxRenderToRealMultiplier *
    ltx23LoraMultiplier *
    seedanceMultiplier *
    imageCountMultiplier;
  const fullCredits = roundToDecimalPlaces(entry.credits * multiplier, 1);
  // Discount the base credits before multipliers (mirrors server) so it applies exactly once; charge stays server-side.
  const promo =
    priceName && !options?.isImportedModel
      ? resolvePromotionForModel(options?.promotions ?? [], priceName, entry.credits, entry.modelVariant)
      : undefined;
  if (!promo) {
    return { isByok, credits: fullCredits, isPerSecond: entry.isPerSecond };
  }
  return {
    isByok,
    credits: roundToDecimalPlaces(promo.newBasePrice * multiplier, 1),
    fullCredits,
    promo: { expiresAt: promo.expiresAt },
    isPerSecond: entry.isPerSecond,
  };
};

/**
 * Resolves the total credit cost for a single model run by multiplying
 * per-second rates by the parsed duration. For flat-rate models the
 * credits are returned unchanged.
 *
 * Mirrors the server's `getModelRunCost` logic.
 */
export function resolveModelRunCost(
  model: Partial<Model>,
  modelPricesMaps: ModelPricesMaps,
  modelInput: ModelBaseNodeData['params'],
  normalizeImportedModelNames?: boolean,
  promotions?: ModelPromotion[],
  isImportedModel?: boolean,
  isVideoOutput?: boolean,
): ResolvedModelPrice | undefined {
  const info = getModelPriceInfo(model, modelPricesMaps, modelInput, {
    normalizeImportedModelNames,
    promotions,
    isImportedModel,
    isVideoOutput,
  });
  if (!info) return undefined;
  if (!info.isPerSecond) return info;

  const durationSeconds =
    parseDurationSeconds(modelInput as Record<string, unknown>) ?? resolveFrameCountSeconds(model.name, modelInput);
  if (!durationSeconds) {
    // Server charges 0 in this case (model-price.service.ts#getModelRunCost), but for the display
    // we keep the per-second rate so the UI can render "X credits/sec" — the user needs the rate
    // to estimate the cost of their duration choice.
    logger.warn(
      'resolveModelRunCost: per-second model has no parseable duration; surfacing per-second rate as estimate',
      { model, modelInput },
    );
    return info;
  }
  return {
    ...info,
    credits: info.credits * durationSeconds,
    fullCredits: info.fullCredits === undefined ? undefined : info.fullCredits * durationSeconds,
    isPerSecond: false,
  };
}

export const hasEditingPermissions = (role: WorkflowRole | null, data: { isLocked?: boolean }) => {
  if (role !== 'editor') {
    return false;
  }
  if (data.isLocked) {
    return false;
  }

  return true;
};

export const uploadFile = async (
  acceptedFiles: File[],
  setUploadProgress: (progress: number) => void,
  uploadSuccess: (data: MediaAsset) => void,
  setHasError: (hasError: boolean) => void,
  runType: RunType,
) => {
  if (acceptedFiles.length > 0) {
    const file = acceptedFiles[0];

    const formData = new FormData();
    formData.append('file', file);
    const mimeType = file.type || getMimeTypeFromExtension(file.name);
    if (mimeType) {
      formData.append('type', mimeType);
    }
    // formData.append("media_metadata", true);

    try {
      setUploadProgress(0); // Initialize the progress bar
      const response = await safeAxios.post<UploadAssetResponse>(API_ROUTES.assets.uploadAsset(), formData, {
        params: { runType },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 95) / (progressEvent.total || (progressEvent.loaded || 1) * 100),
          );
          setUploadProgress(progress);
        },
      });

      useWorkflowStore.getState().mergeResolvedAssets(response.data.resolvedAssets ?? {});
      uploadSuccess(response.data.asset);
    } catch (error) {
      logger.error('Error uploading file', error);
      setHasError(true);
    } finally {
      setUploadProgress(0); // Reset the progress bar
    }
  }
};

function VideoPlayer({ mediaUrl }: { mediaUrl: string }) {
  return (
    <Box sx={{ position: 'relative' }}>
      <video src={mediaUrl} crossOrigin="anonymous" draggable="false" width="100%" controls loop />
    </Box>
  );
}

export function renderMediaElement(
  mediaUrl: string,
  mediaType: string,
  containerSize: {
    w: number;
    h: number;
  },
) {
  if (!mediaUrl || !mediaType) {
    return null; // or a default placeholder
  }
  switch (true) {
    case mediaType.includes('video'):
      return <VideoPlayer mediaUrl={mediaUrl} />;
    case mediaType.includes('image'):
      return (
        <>
          <img
            draggable="false"
            src={mediaUrl}
            width="100%"
            height="100%"
            alt="Media"
            style={{ display: 'block', objectFit: 'contain' }}
          />
        </>
      );
    case mediaType.includes('audio'):
      return (
        <audio
          draggable="false"
          crossOrigin="anonymous"
          src={mediaUrl}
          controls
          style={{ display: 'block', width: '100%' }}
        />
      );
    case mediaType.includes('3D'):
      return (
        <Box className="nodrag">
          <ThreeDeeViewer objUrl={mediaUrl} containerSize={containerSize} />
        </Box>
      );
    // Add more cases for other types
    default:
      return <TextStdRg color="inherit">Preview</TextStdRg>;
  }
}
